/**
 * CreateMeioPagamentoDialog - Dialog reutilizável para criação de meios de pagamento
 * 
 * Extraído da página FinancialPaymentMethods para uso inline nos wizards de importação.
 * Utiliza os hooks existentes useFinanceiroMeiosPagamento e useFinanceiroContaCorrente.
 * 
 * @author Sistema
 * @version 1.0.0
 * RELEASE: 2026-03-22 v1.0.0 - Criação do dialog reutilizável
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import {
  useFinanceiroMeiosPagamento,
  TIPOS_PAGAMENTO,
  type TipoPagamento,
} from '@/hooks/useFinanceiroMeiosPagamento';
import { useFinanceiroContaCorrente } from '@/hooks/useFinanceiroContaCorrente';
import { PaymentMethodIcon, DefaultPaymentIcon } from '@/components/icons/PaymentMethodIcons';

/** Props do dialog */
interface CreateMeioPagamentoDialogProps {
  /** Controle de visibilidade */
  open: boolean;
  /** Callback de mudança de estado */
  onOpenChange: (open: boolean) => void;
  /** Callback chamado após criação bem-sucedida */
  onSuccess?: () => void;
}

/**
 * Dialog para criação de novo meio de pagamento
 * Reutiliza os hooks CRUD existentes do módulo financeiro
 */
export function CreateMeioPagamentoDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreateMeioPagamentoDialogProps) {
  // Estados do formulário
  const [nome, setNome] = useState('');
  const [tipoPagamento, setTipoPagamento] = useState<TipoPagamento>('pix');
  const [contaCorrenteId, setContaCorrenteId] = useState<string>('none');
  const [instrucoes, setInstrucoes] = useState('');

  // Hooks CRUD
  const { createMeio } = useFinanceiroMeiosPagamento();
  const { contas } = useFinanceiroContaCorrente();

  // Contas ativas para o select
  const contasAtivas = contas.filter(c => c.is_active);

  /** Reseta o formulário */
  const resetForm = () => {
    setNome('');
    setTipoPagamento('pix');
    setContaCorrenteId('none');
    setInstrucoes('');
  };

  /** Handler de submissão */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim() || contaCorrenteId === 'none') return;

    try {
      await createMeio.mutateAsync({
        nome: nome.trim(),
        tipo_pagamento: tipoPagamento,
        conta_corrente_id: contaCorrenteId,
        instrucoes: instrucoes.trim() || undefined,
        is_active: true,
      });

      resetForm();
      onOpenChange(false);
      onSuccess?.();
    } catch {
      // Erro tratado pelo hook (toast)
    }
  };

  /** Renderiza ícone do tipo de pagamento selecionado */
  const renderPaymentIcon = (tipo: string) => {
    const IconComponent = PaymentMethodIcon[tipo] || DefaultPaymentIcon;
    return <IconComponent className="h-4 w-4" />;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Novo Meio de Pagamento</DialogTitle>
          <DialogDescription>
            Cadastre um novo meio de pagamento para uso no sistema financeiro
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Nome */}
          <div className="space-y-2">
            <Label htmlFor="mp-nome">Nome *</Label>
            <Input
              id="mp-nome"
              placeholder="Ex: PIX Banco do Brasil"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              required
            />
          </div>

          {/* Tipo de Pagamento */}
          <div className="space-y-2">
            <Label htmlFor="mp-tipo">Tipo de Pagamento *</Label>
            <Select value={tipoPagamento} onValueChange={(v) => setTipoPagamento(v as TipoPagamento)}>
              <SelectTrigger id="mp-tipo">
                <SelectValue placeholder="Selecione o tipo" />
              </SelectTrigger>
              <SelectContent>
                {TIPOS_PAGAMENTO.map((tipo) => (
                  <SelectItem key={tipo.value} value={tipo.value}>
                    <div className="flex items-center gap-2">
                      {renderPaymentIcon(tipo.value)}
                      <span>{tipo.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Conta Corrente (obrigatória) */}
          <div className="space-y-2">
            <Label htmlFor="mp-conta">Conta Corrente *</Label>
            <Select value={contaCorrenteId} onValueChange={setContaCorrenteId}>
              <SelectTrigger id="mp-conta">
                <SelectValue placeholder="Selecione a conta corrente" />
              </SelectTrigger>
              <SelectContent>
                {contasAtivas.map((conta) => (
                  <SelectItem key={conta.id} value={conta.id}>
                    {conta.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {contaCorrenteId === 'none' && (
              <p className="text-xs text-destructive">Selecione uma conta corrente</p>
            )}
          </div>

          {/* Instruções (opcional) */}
          <div className="space-y-2">
            <Label htmlFor="mp-instrucoes">Instruções</Label>
            <Textarea
              id="mp-instrucoes"
              placeholder="Instruções adicionais para pagamento..."
              value={instrucoes}
              onChange={(e) => setInstrucoes(e.target.value)}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!nome.trim() || contaCorrenteId === 'none' || createMeio.isPending}
            >
              {createMeio.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Criar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
